import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, uploadFile, fileUrl } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU, NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Trash2, Upload, FileText, X } from "lucide-react";
import { FASE, JENJANG, MAPEL_CP } from "./_shared";

const F_EMPTY = { jenjang: "", fase: "", mapel: "" };

function CPFilterBar({ pending, setPending, onApply, onReset }) {
  return (
    <section className="pp-card p-5 mb-6 space-y-3" data-testid="cp-filter">
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide mr-1">Jenjang:</span>
        <button onClick={() => setPending({ ...pending, jenjang: "" })}
          className={`text-xs px-3 py-1.5 rounded-lg border ${!pending.jenjang ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
          data-testid="cp-filter-jenjang-all">Semua</button>
        {JENJANG.map((j) => (
          <button key={j} onClick={() => setPending({ ...pending, jenjang: j })}
            className={`text-xs px-3 py-1.5 rounded-lg border ${pending.jenjang === j ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
            data-testid={`cp-filter-jenjang-${j}`}>{j}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="min-w-[180px] flex-1">
          <label className="pp-label">Fase</label>
          <select className="pp-input" value={pending.fase} onChange={(e) => setPending({ ...pending, fase: e.target.value })} data-testid="cp-filter-fase">
            <option value="">Semua Fase</option>
            {FASE.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="pp-label">Mata Pelajaran</label>
          <select className="pp-input" value={pending.mapel} onChange={(e) => setPending({ ...pending, mapel: e.target.value })} data-testid="cp-filter-mapel">
            <option value="">Semua Mata Pelajaran</option>
            {MAPEL_CP.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={onApply} className="pp-btn-primary" data-testid="cp-filter-apply">Terapkan</button>
          <button onClick={onReset} className="pp-btn-secondary" data-testid="cp-filter-reset">Reset</button>
        </div>
      </div>
    </section>
  );
}

function useCpFilter() {
  const [pending, setPending] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const apply = () => setApplied({ ...pending });
  const reset = () => { setPending(F_EMPTY); setApplied(F_EMPTY); };
  return { pending, setPending, applied, apply, reset };
}

export function CPKepsek() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ jenjang: "TKLB", mata_pelajaran: MAPEL_CP[0], fase: "Fase A", file_id: null });
  const [uploading, setUploading] = useState(false);
  const { pending, setPending, applied, apply, reset } = useCpFilter();

  const load = useCallback(async () => {
    try {
      const params = {}; if (applied.jenjang) params.jenjang = applied.jenjang;
      const { data } = await api.get("/capaian-pembelajaran", { params }); setItems(data);
    } catch (e) { console.error(e); }
  }, [applied.jenjang]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((c) =>
    (!applied.fase || c.fase === applied.fase) &&
    (!applied.mapel || c.mata_pelajaran === applied.mapel)
  ), [items, applied.fase, applied.mapel]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setForm({ ...form, file_id: r.id }); toast.success("File CP diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_id) return toast.error("Upload file CP dulu");
    try { await api.post("/capaian-pembelajaran", form); toast.success("CP tersimpan"); setShow(false); setForm({ ...form, file_id: null, mata_pelajaran: MAPEL_CP[0] }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus CP?")) return; await api.delete(`/capaian-pembelajaran/${id}`); toast.success("Dihapus"); load(); };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Capaian Pembelajaran (CP)" action={
        <button onClick={() => setShow(true)} className="pp-btn-primary" data-testid="open-cp">
          <Plus className="w-4 h-4" /> Upload CP
        </button>
      } />

      <CPFilterBar pending={pending} setPending={setPending} onApply={apply} onReset={reset} />

      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold">Upload Capaian Pembelajaran</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Jenjang</label>
              <select className="pp-input" value={form.jenjang} onChange={(e) => setForm({ ...form, jenjang: e.target.value })}>
                {JENJANG.map((j) => <option key={j}>{j}</option>)}
              </select></div>
            <div><label className="pp-label">Fase</label>
              <select className="pp-input" value={form.fase} onChange={(e) => setForm({ ...form, fase: e.target.value })}>
                {FASE.map((f) => <option key={f}>{f}</option>)}
              </select></div>
            <div className="sm:col-span-2"><label className="pp-label">Mata Pelajaran *</label>
              <select required className="pp-input" value={form.mata_pelajaran} onChange={(e) => setForm({ ...form, mata_pelajaran: e.target.value })} data-testid="cp-form-mapel">
                {MAPEL_CP.map((m) => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <div className="sm:col-span-2"><label className="pp-label">File CP (PDF) *</label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.file_id ? "Ganti File" : "Pilih PDF")}
                <input type="file" accept=".pdf" onChange={upload} className="hidden" /></label>
              {form.file_id && <span className="ml-3 text-sm text-emerald-600">✓ File siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-cp">Simpan CP</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Jenjang</th>
            <th className="text-left px-5 py-3">Fase</th>
            <th className="text-left px-5 py-3">Mata Pelajaran</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Belum ada CP.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="pp-row">
                <td className="px-5 py-3"><span className="pp-badge-info">{c.jenjang}</span></td>
                <td className="px-5 py-3">{c.fase}</td>
                <td className="px-5 py-3 font-medium">{c.mata_pelajaran}</td>
                <td className="px-5 py-3"><a href={fileUrl(c.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs"><FileText className="w-3.5 h-3.5 inline" /> Lihat</a></td>
                <td className="px-5 py-3 text-right"><button onClick={() => del(c.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

export function CPGuru() {
  const [items, setItems] = useState([]);
  const { pending, setPending, applied, apply, reset } = useCpFilter();

  const load = useCallback(async () => {
    try {
      const params = {}; if (applied.jenjang) params.jenjang = applied.jenjang;
      const { data } = await api.get("/capaian-pembelajaran", { params }); setItems(data);
    } catch (e) { console.error(e); }
  }, [applied.jenjang]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((c) =>
    (!applied.fase || c.fase === applied.fase) &&
    (!applied.mapel || c.mata_pelajaran === applied.mapel)
  ), [items, applied.fase, applied.mapel]);

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Capaian Pembelajaran" />
      <CPFilterBar pending={pending} setPending={setPending} onApply={apply} onReset={reset} />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Jenjang</th>
            <th className="text-left px-5 py-3">Fase</th>
            <th className="text-left px-5 py-3">Mata Pelajaran</th>
            <th className="text-left px-5 py-3">File</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400 italic">Belum ada CP.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="pp-row">
                <td className="px-5 py-3"><span className="pp-badge-info">{c.jenjang}</span></td>
                <td className="px-5 py-3">{c.fase}</td>
                <td className="px-5 py-3 font-medium">{c.mata_pelajaran}</td>
                <td className="px-5 py-3"><a href={fileUrl(c.file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-xs"><FileText className="w-3.5 h-3.5 inline" /> Lihat</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
